import { logUsage } from 'owa-analytics';
import { createLazyOrchestrator } from 'owa-bundling';
import { lazyInvokeSearch } from 'owa-calendar-search-orchestration';
import getSearchQueryString from 'owa-calendar-search-orchestration/lib/selectors/getSearchQueryString';
import { setSearchState } from 'owa-calendar-search-view-store';
import { getGuid } from 'owa-guid';
import { startSearch, startSearchSession } from 'owa-search-actions';
import { SearchScenarioId, getScenarioStore } from 'owa-search-store';
import type ConnectedAccountInfo from 'owa-search-service/lib/data/schema/ConnectedAccountInfo';
import { getPageContext } from 'owa-search-service/lib/helpers/pageContext';
import setCurrentSearchQueryId from 'owa-search/lib/mutators/setCurrentSearchQueryId';
import setNextSearchQueryId from 'owa-search/lib/mutators/setNextSearchQueryId';
import {
    isConnectedAccount,
    getOWAConnectedAccountTokenString,
    getOWAConnectedAccount,
} from 'owa-accounts-store';
import {
    CalendarSearchListViewState,
    lazyInitSearchResults,
    lazySetSearchMode,
} from 'owa-calendar-searchresults-store';
import {
    incrementSearchNumber,
    initSearchDatapoint,
    getCalendarSearchStore,
    getFromDate,
    getToDate,
} from 'owa-calendar-search-store';

/**
 * Starts search on user input by invoking the substrate API
 */
export const startSearchOrchestrator = createLazyOrchestrator(
    startSearch,
    'CLONE_START_CALENDAR_SEARCH',
    async actionMessage => {
        if (actionMessage.scenarioId != SearchScenarioId.Calendar) {
            return null;
        }
        // Set Search loading state
        const setSearchMode = await lazySetSearchMode.import();
        setSearchMode(CalendarSearchListViewState.Loading);

        // set the module state to show search view
        setSearchState(true);

        // Initialize / Reset search results store
        const initSearchResults = await lazyInitSearchResults.import();
        initSearchResults();

        let searchStore = getScenarioStore(SearchScenarioId.Calendar);

        // If we are here then we must have something to search!
        if (searchStore.searchText.length == 0 && searchStore.suggestionPillIds.length == 0) {
            throw new Error('Calendar: StartSearch should not be called with an empty search!!');
        }

        // SearchSessionGuid is set in startSearchSession as a part of initialization code.
        // If it was not set due to lazy loading of the script and user intiating the search mean while
        // we will call startSearchSession here again.
        if (!searchStore.searchSessionGuid) {
            startSearchSession(
                actionMessage.actionSource,
                true /* shouldStartSeach */,
                SearchScenarioId.Calendar
            );
            return null;
        }

        incrementSearchNumber();
        let queryString = getSearchQueryString();

        // We're starting a new search query for the first page so we need to update the query IDs.
        setCurrentSearchQueryId(searchStore.nextSearchQueryId, SearchScenarioId.Calendar);
        setNextSearchQueryId(getGuid(), SearchScenarioId.Calendar);

        let calendarSearchStore = getCalendarSearchStore();

        // For search v1 - we will search in all selected calendars - filter out the non-local calendars
        let selectedCalendars = calendarSearchStore.calendarFoldersScope.filter(
            item => item.isSelected == true
        );
        let calendarFolderIds: string[] = [];
        for (const searchScope of selectedCalendars) {
            calendarFolderIds.push(searchScope.folderId);
        }

        // Log CTQ to indicate a search got triggered with no searchable calendar selected.
        if (calendarFolderIds.length == 0) {
            logUsage('NonSearchableCalendarsPresent');
        }

        initSearchDatapoint(queryString.length);

        if (isConnectedAccount(calendarSearchStore.accountSmtpAddress)) {
            logUsage('SearchInConnectedAccount', {
                connectedAccountSearch_1: true,
            });
        }

        let invokeSearch = await lazyInvokeSearch.import();

        // if by this time the searchQueryId had been reset in the store, it means user has exited search, so do not proceed
        if (searchStore.currentSearchQueryId == null) {
            setSearchState(false);
            return null;
        }

        let connectedAccountInfo: ConnectedAccountInfo[] = null;
        const account = getOWAConnectedAccount(calendarSearchStore.accountSmtpAddress);
        if (account) {
            connectedAccountInfo = [
                {
                    mailboxSmtpAddress: account.anchorMailbox,
                    tokenString: getOWAConnectedAccountTokenString(account),
                },
            ];
        }

        let result = invokeSearch(
            getPageContext(0),
            searchStore.searchSessionGuid,
            searchStore.currentSearchQueryId,
            calendarSearchStore.searchNumber,
            calendarSearchStore.searchEndToEndDatapoints[calendarSearchStore.searchNumber - 1],
            queryString,
            calendarSearchStore.includeAttachments,
            calendarFolderIds,
            getFromDate(),
            getToDate(),
            'Asc',
            false,
            searchStore.suggestionPillIds.length > 0 /* queryIncludesPeopleSuggestion */,
            connectedAccountInfo
        );

        return result;
    }
);
